// ────────────────────────────────────────────────
// Helpdesk — типы данных
// ────────────────────────────────────────────────

/* ── Пользователь (краткий) ── */
export interface TicketUser {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

/* ── Компания (краткая) ── */
export interface TicketCompany {
  id: number;
  name: string;
}

/* ── Статусы заявки ──
   Статусы теперь управляются справочником на бэке (/helpdesk/ticket-statuses/),
   поэтому тип — просто строка (code), а карты ниже — fallback для трёх
   системных статусов на случай, когда справочник ещё не загружен.
   Актуальные имена/цвета берите через useTicketStatusDict
   (@shared/lib/ticket-statuses). */
export type TicketStatus = string;

export const TICKET_STATUS_LABELS: Record<string, string> = {
  open: 'Открыта',
  solved: 'Решена',
  closed: 'Закрыта',
};

export const TICKET_STATUS_COLORS: Record<string, string> = {
  open: '#FB8C00',
  solved: '#43A047',
  closed: '#9E9E9E',
};

/* ── Приоритеты ── */
export type TicketPriority = 'low' | 'medium' | 'high' | 'critical';

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Низкий',
  medium: 'Средний',
  high: 'Высокий',
  critical: 'Критический',
};

/* ── Тип заявки (справочник, управляется в настройках Helpdesk) ── */
export interface TicketTypeRef {
  id: number;
  name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

/* ── Каналы регистрации ── */
export type TicketChannel = 'web' | 'email';

export const TICKET_CHANNEL_LABELS: Record<TicketChannel, string> = {
  web: 'Веб-портал',
  email: 'Email',
};

/* ── Вложения ── */
export interface TicketAttachment {
  id: number;
  file: string;
  filename: string;
  size_bytes: number;
  uploaded_at: string;
}

/* ── Перевод сообщения (приходит из messages/translated/) ── */
export interface MessageTranslation {
  original: string;
  translated: string;
  is_translated: boolean;
  target_lang: string;
}

/* ── Сообщение ── */
export interface TicketMessage {
  id: number;
  author: TicketUser;
  body: string;
  is_internal: boolean;
  attachments: TicketAttachment[];
  created_at: string;
  // Заполняется только ответом эндпоинта messages/translated/.
  translation?: MessageTranslation;
}

/* ── История изменений ── */
export interface TicketHistory {
  id: number;
  changed_by: TicketUser | null;
  field_name: string;
  old_value: string;
  new_value: string;
  changed_at: string;
}

/* ── SLA тикета ── */
export interface TicketSLA {
  id: number;
  ticket: number;
  policy: number;
  first_response_deadline: string | null;
  resolution_deadline: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  first_response_breached: boolean;
  resolution_breached: boolean;
}

/* ── Тикет (список) ── */
export interface TicketListItem {
  id: number;
  number: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  channel: TicketChannel;
  ticket_type: TicketTypeRef | null;
  user: TicketUser;
  company: TicketCompany | null;
  assigned_to: TicketUser | null;
  // SLA уже отдаётся в TicketListSerializer на бэке — нужно тут,
  // чтобы в таблице тикетов рисовать колонку «SLA».
  sla: TicketSLA | null;
  created_at: string;
  updated_at: string;
  solved_at: string | null;
  sender_email: string;
}

/* ── Тикет (детальный) ── */
export interface TicketDetail extends TicketListItem {
  description: string;
  parent_ticket: number | null;
  messages: TicketMessage[];
  attachments: TicketAttachment[];
  history: TicketHistory[];
}

/* ── Создание тикета ── */
export interface TicketCreatePayload {
  subject: string;
  description: string;
  priority?: TicketPriority;
  channel?: TicketChannel;
  // id из справочника типов (на бэке FK принимается под своим именем).
  ticket_type?: number | null;
}

/* ── Создание тикета оператором ──
   Имена полей = TicketOperatorCreateSerializer на бэке: FK принимаются
   под своими именами (user/company/assigned_to), без суффикса _id. */
export interface TicketOperatorCreatePayload extends TicketCreatePayload {
  user: number;
  company?: number;
  sender_email?: string;
  assigned_to?: number;
}

/* ── Обновление тикета (PATCH) ──
   Поля = TicketUpdateSerializer: subject/description/priority/status/
   assigned_to/company. FK — без суффикса _id. */
export interface TicketUpdatePayload {
  subject?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  ticket_type?: number | null;
  assigned_to?: number | null;
  company?: number | null;
}

/* ── Создание сообщения ── */
export interface MessageCreatePayload {
  body: string;
  is_internal?: boolean;
}

/* ── Фильтры списка ── */
export interface TicketFilters {
  status: string;
  priority: string;
  assigned_to: string;
  channel: string;
  ticket_type: string;
  search: string;
  date_from: string;
  date_to: string;
  ordering: string;
  /** '' — мои филиалы (по умолчанию), 'all' — все, '<id>' — конкретный */
  branch: string;
}

/* ── Пагинированный ответ ── */
export interface PaginatedTickets {
  count: number;
  next: string | null;
  previous: string | null;
  results: TicketListItem[];
}

// Примечание: типы настроек SLA/маршрутизации живут в
// helpdeskSettingsInterfaces.ts (их использует helpdesk-settings.service).
// Прежние дубли SLAPolicy/RoutingRule здесь были не подключены и содержали
// устаревшие имена полей — удалены, чтобы не импортировать их по ошибке.
